import React, { useEffect, useState } from 'react';
import { Dialog, LinearProgress } from '@mui/material';

import { COMPLETION_ANIMATION_DELAY } from '../../../utils/constants';
import IconContainer from '../FtuxComponent/IconContainer';

import './_savingProgressDialog.scss';

interface SavingProgressDialogProps {
  open: boolean;
  title?: string;
  icon?: string;
  description?: string;
  hideProgress?: boolean;
}

const SavingProgressDialog: React.FC<SavingProgressDialogProps> = ({
  open,
  title = 'Saving Your Progress!',
  icon = '💾',
  description = 'Hang on! Saving your data...',
  hideProgress = false,
}) => {
  const [progress, setProgress] = useState(0);
  const [isCompleting, setIsCompleting] = useState(false);
  const [shouldShow, setShouldShow] = useState(false);

  useEffect(() => {
    if (open) {
      setShouldShow(true);
      setIsCompleting(false);
      setProgress(0);
    } else if (shouldShow) {
      // When dialog is requested to close, trigger completion
      setIsCompleting(true);
    }
  }, [open]);

  useEffect(() => {
    if (!shouldShow || hideProgress) {
      return;
    }

    let timer: number;

    if (isCompleting) {
      // Quickly complete to 100%
      timer = window.setInterval(() => {
        setProgress((prevProgress) => {
          if (prevProgress >= 100) {
            window.clearInterval(timer);
            // After reaching 100%, wait a bit before actually closing
            window.setTimeout(() => {
              setShouldShow(false);
            }, COMPLETION_ANIMATION_DELAY);
            return 100;
          }
          return Math.min(100, prevProgress + 10);
        });
      }, 50);
    } else {
      // Normal progress behavior up to 90%
      timer = window.setInterval(() => {
        setProgress((prevProgress) => {
          if (prevProgress >= 90) {
            window.clearInterval(timer);
            return 90;
          }
          const increment = Math.floor(Math.random() * 11) + 5;
          return Math.min(90, prevProgress + increment);
        });
      }, 800);
    }

    return () => {
      window.clearInterval(timer);
    };
  }, [shouldShow, hideProgress, isCompleting]);

  return (
    <Dialog
      open={shouldShow}
      PaperProps={{
        className: 'saving-progress-dialog-paper',
      }}>
      <div className="saving-progress-dialog-icon">
        <IconContainer icon={icon} bgColor="var(--surface-default)" />
      </div>
      <h2 className="saving-progress-dialog-title">{title}</h2>
      <p className="saving-progress-dialog-description">{description}</p>
      {!hideProgress && (
        <div className="saving-progress-dialog-progress-container">
          <LinearProgress variant="determinate" value={progress} className="saving-progress-dialog-progress" />
          <div className="saving-progress-dialog-percentage">{Math.round(progress)}%</div>
        </div>
      )}
    </Dialog>
  );
};

export default SavingProgressDialog;
